import { Component, OnInit, OnDestroy, Input } from '@angular/core';
import { trigger, transition, useAnimation } from '@angular/animations';
import { timer, forkJoin, Subscription, Observable, Subject } from 'rxjs';
import { map as rxmap, takeUntil } from 'rxjs/operators';
import { isNil, assign, map } from 'lodash';

import { AuthService } from '@app/shared/services/auth.service';
import { AzureBlobStorageService } from '@app/shared/services/api/azure-blob-storage.service';
import { OverlayLoaderService } from '@app/shared/services/overlay-loader.service';
import { videoFileTypes } from '@app/shared/constants';
import { fadeIn, fadeOut } from './carousel.animations';
import { SafeUrl } from '@angular/platform-browser';

@Component({
  selector: 'app-carousel',
  templateUrl: './carousel.component.html',
  styleUrls: ['./carousel.component.scss'],
  animations: [
    trigger('carouselAnimation', [
      transition('void => *', [useAnimation(fadeIn, { params: { time: '300ms' } })]),
      transition('* => void', [useAnimation(fadeOut, { params: { time: '300ms' } })]),
    ]),
  ],
})
export class CarouselComponent implements OnInit, OnDestroy {
  @Input() imageCount = 1;
  @Input() baseURL = '';
  @Input() assets: CarouselAsset[] = [];

  protected unsubscribe$: Subject<void> = new Subject<void>();

  public files: CarouselAsset[] = [];

  public currentSlide = -1;

  private interval: Subscription = new Subscription();

  constructor(
    public authService: AuthService,
    private azureBlobStorageService: AzureBlobStorageService,
    private overlayLoaderService: OverlayLoaderService,
  ) {}

  public ngOnInit(): void {
    this.loadAssets();
  }

  public ngOnDestroy(): void {
    if (!isNil(this.interval)) {
      this.interval.unsubscribe();
    }
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  public onPreviousClick(): void {
    if (isNil(this.interval)) {
      this.currentSlide--;
      this.startTimer();
    }
    const previous = this.currentSlide - 1;
    this.currentSlide = previous < 0 ? this.files.length - 1 : previous;
  }

  public onNextClick(): void {
    if (isNil(this.interval)) {
      this.currentSlide--;
      this.startTimer();
    }
    const next = this.currentSlide + 1;
    this.currentSlide = next === this.files.length ? 0 : next;
  }

  public navToPage(imageNumber: number): void {
    if (isNil(this.interval)) {
      imageNumber--;
      this.startTimer();
    }
    this.currentSlide = imageNumber;
  }

  public isVideoFile(fileName: string): boolean {
    return videoFileTypes.includes(fileName.split('.')[1]);
  }

  public videoClicked(isVideo: boolean): void {
    if (isVideo && !isNil(this.interval)) {
      this.interval.unsubscribe();
      this.interval = new Subscription();
    }
  }

  private loadAssets(): void {
    if (isNil(this.assets) || this.assets.length === 0) {
      return;
    }
    this.files = map(this.assets, (asset: CarouselAsset) => assign(asset, { src: null, isVideo: this.isVideoFile(asset.asset) }));
    this.loadVideos();
    this.loadImages();
  }

  private loadImages(): void {
    const imageDownloads: AssetDownload[] = this.assets
      .filter((a) => !!a.asset && !this.isVideoFile(a.asset))
      .map((rec) => ({
        index: rec.index,
        blob: this.azureBlobStorageService.getBlobImageSafeUrl(`${this.baseURL}/${rec.asset}`),
      }));
    this.overlayLoaderService.isLoading = true;
    forkJoin(imageDownloads.map((a: AssetDownload) => a.blob))
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((images: SafeUrl[]) => {
        images.forEach((image: SafeUrl, i: number) => {
          this.files = this.files.map((a) => (a.index === imageDownloads[i].index ? assign(a, { src: image }) : a));
        });
        this.overlayLoaderService.isLoading = false;
        this.startTimer();
      });
  }

  private loadVideos(): void {
    const videoDownloads: AssetDownload[] = this.assets
      .filter((a: CarouselAsset) => !!a.asset && this.isVideoFile(a.asset))
      .map((rec: CarouselAsset) => ({
        index: rec.index,
        blob: this.azureBlobStorageService.getBlobImageSafeUrl(`${this.baseURL}/${rec.asset}`),
      }));
    videoDownloads.forEach((v: AssetDownload, i: number) => {
      v.blob.pipe(takeUntil(this.unsubscribe$)).subscribe((video) => {
        this.files = this.files.map((a: CarouselAsset) => (a.index === videoDownloads[i].index ? assign(a, { src: video }) : a));
      });
    });
  }

  private startTimer(): void {
    this.interval = timer(0, 5000)
      .pipe(
        rxmap(() => this.onNextClick()),
        takeUntil(this.unsubscribe$),
      )
      .subscribe();
  }
}

export interface CarouselAsset {
  index: number;
  asset: string;
}

export interface AssetDownload {
  index: number;
  blob: Observable<SafeUrl>;
}
